import React, { useState } from 'react';
import { match } from 'ts-pattern';

import { BookingBreakdownCard, usePollBooking } from '@/booking';
import { isBookingCancelled } from '@/booking/helpers/status';
import { useBookingStatus } from '@/booking/hooks/use-booking-status.hook';
import { BookingRequest } from '@/booking/interfaces';
import { useTranslation } from '@/i18n';
import { Markdown } from '@/markdown';
import { Button } from '@/ui/controls';
import { Divider } from '@/ui/layout';
import { Spacer } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';
import { BodySmall } from '@/ui/typography';

import { BookingCancellationModal } from './BookingCancellationModal.component';

interface BookingManagementCardProps extends BookingRequest {
  cancellable: boolean;
}

export const BookingManagementCard: React.FC<BookingManagementCardProps> = (
  props
) => {
  const [isCancellationModalVisible, setIsCancellationModalVisible] =
    useState(false);
  const closeCancellationModal = () => setIsCancellationModalVisible(false);
  const showCancellationModal = () => setIsCancellationModalVisible(true);

  const { t } = useTranslation('bookingManagement');

  const {
    componentProperties: { bookingManagement },
  } = useTheme();

  const bookingStatus = useBookingStatus({
    bookingId: props.bookingId,
    hotelReferenceId: props.hotelSlug,
  });
  usePollBooking({
    bookingId: props.bookingId,
    hotelReferenceId: props.hotelSlug,
  });

  if (!bookingStatus) {
    return null;
  }

  const isCancelled = isBookingCancelled(bookingStatus.status);

  const footer = match({ status: bookingStatus?.status, props, isCancelled })
    .with({ status: 'CANCEL_RECEIVED' }, () => undefined)
    .with({ props: { cancellable: true }, isCancelled: false }, () => (
      <>
        <Spacer
          s="s"
          css={{ height: bookingManagement?.bookingCard?.body?.rowGap }}
        />
        <Button
          onClick={showCancellationModal}
          variant="destructive"
          {...bookingManagement?.bookingCard?.button}
        >
          {t('managementCard.cancelButtonLabel')}
        </Button>
        <BookingCancellationModal
          onRequestClose={closeCancellationModal}
          isOpen={isCancellationModalVisible}
          booking={props}
        />
        <Spacer
          s="s"
          css={{ height: bookingManagement?.bookingCard?.body?.rowGap }}
        />
        <Divider />
        <Spacer
          s="s"
          css={{ height: bookingManagement?.bookingCard?.body?.rowGap }}
        />
        <BodySmall>
          <Markdown source={t('managementCard.activeBookingMessage')} />
        </BodySmall>
      </>
    ))
    .with({ isCancelled: true }, () => (
      <>
        <Spacer
          s="s"
          css={{ height: bookingManagement?.bookingCard?.body?.rowGap }}
        />
        <BodySmall>
          <Markdown source={t('managementCard.cancelledMessage')} />
        </BodySmall>
      </>
    ))
    .otherwise(() => (
      <>
        <Spacer
          s="s"
          css={{ height: bookingManagement?.bookingCard?.body?.rowGap }}
        />
        <BodySmall>
          <Markdown source={t('managementCard.nonCancellableMessage')} />
        </BodySmall>
      </>
    ));

  return <BookingBreakdownCard {...props} footer={footer} />;
};
